import "./App.css";
import "./color-constants.css";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import ChatBody from "./Core/Components/ChatBody/ChatBody";
import AdminRoute from "./Core/Components/Admin/AdminRoute";
import UserList from "./Core/Components/Admin/UserList/UserList";
import UserForm from "./Core/Components/Admin/UserForm/UserForm";
import BulkImport from "./Core/Components/Admin/BulkImport/BulkImport";
import NotificationCampaign from "./Core/Components/Admin/NotificationCampaign/NotificationCampaign";
import AuditLog from "./Core/Components/Admin/AuditLog/AuditLog";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Introduction from "./Core/Components/Introduction/Introduction";
import LoginComponent from "./Core/Components/Auth/LoginComponent/LoginComponent";
import Guide from "./Core/Components/Guide/Guide";
import Consent from "./Core/Components/Consent/Consent";
import DeviceReadinessConsent from "./Core/Components/Device_readiness_Consent/Device_readiness_Consent";
import DeviceReadinessIntro from "./Core/Components/Device_readiness_Intro/Device_readiness_Intro";
import DeviceReadinessForm from "./Core/Components/Device_readiness_Form/Device_readiness_Form";
import DeviceReadinessEnd from "./Core/Components/Device_readiness_End/Device_readiness_End";
import DeviceReadinessRecord from "./Core/Components/Device_readiness_Record/Device_readiness_Record";
import NetworkNotification from "./Shared/Components/NetworkNotification/NetworkNotification";
import serviceWorkerRegistration from "./serviceWorkerRegistration";
import RegisterComponent from "./Core/Components/Auth/RegisterComponent/RegisterComponent";
import RegisterSuccessComponent from "./Core/Components/Auth/RegisterSuccessComponent/RegisterSuccessComponent";
import { getDefaultLanguage, isNeGDDeployment, setDefaultLanguage, setDeferredPrompt } from "./Actions/Utils";
import { checkMiddleWareHealth } from "./Actions/ChatActions";
import StaticHTML from "./Core/Components/StaticHTML/StaticHTML";
import { trackEvent } from "./Actions/Utils";
import KbSelection from "./Core/Components/kbSelection/kbSelection";
import {offlineText, onlineText, retryText, tryAgainText} from "./NewConstants/NetworkConstants";
import { liveText, downText, retryLiveText, tryAgainLiveText } from "./NewConstants/MiddlewareConstants";
import './firebaseConfig';
import { SERVER_URL } from "./ENV";
import IndicLang from "./Core/Components/IndicLang/IndicLang";
import LanguageCheck from "./Core/Components/LanguageCheck/LanguageCheck";
import Splash from "./Core/Components/Splash/Splash";
import PwaInstall from "./Core/Components/PwaInstall/PwaInstall";
import AppError from "./Core/Components/AppError/AppError";
import NotificationManager from "./Core/Components/NotificationManager/NotificationManager";

function App() {
  const [selectedLanguage, setSelectedLanguage] = useState(
    getDefaultLanguage() || "hi"
  );
  const [textSize, setTextSize] = useState(1.125);
  const audioRef = useRef();
  const middlewareIntervalRef = useRef(null);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOnlinePopup, setShowOnlinePopup] = useState(false);
  const [isTryingToConnect, setIsTryingToConnect] = useState(false);
  const [showStatusBar, setShowStatusBar] = useState(false);
  const [showMiddlewarePopup, setShowMiddlewarePopup] = useState(null);
  const [middlewareLiveStatus, setMiddlewareLiveStatus] = useState(null);
  const [retryMiddlewareStatus, setRetryMiddlewareStatus] = useState(null);
  const [startTour, setStartTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const serverSchedullingFlag = process.env.REACT_APP_SERVER_SCHEDULING_FLAG === "true";

  const getCurrentHour = useCallback(() => {
    const now = new Date();
    return now.getHours();
  }, []);

  const isRestrictedTime = useCallback(() => {
    const hour = getCurrentHour();
    return hour >= 0 && hour < 8;
  }, [getCurrentHour]);

  const decreaseTextSize = useCallback((event) => {
    event.preventDefault();
    setTextSize((prevSize) => Math.max(prevSize - 0.125, 0.5));
    trackEvent("font_size_changed", "Click", "Number of times users clicked on font size icon (Decrease)");
  }, []);

  const increaseTextSize = useCallback((event) => {
    event.preventDefault();
    setTextSize((prevSize) => Math.min(prevSize + 0.125, 3));
    trackEvent("font_size_changed", "Click", "Number of times users clicked on font size icon (Increase)");
  }, []);

  const middlewareHealth = useCallback(async () => {
    const res = await checkMiddleWareHealth();
    setShowMiddlewarePopup(res);
    setMiddlewareLiveStatus(String(res));
    return res;
  }, []);

  const tryToReconnect = useCallback(() => {
    setIsTryingToConnect(true);
    const timeoutId = setTimeout(() => {
      if (navigator.onLine) {
        setIsOnline(true);
        setShowOnlinePopup(true);
      } else {
        setIsTryingToConnect(false);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, []);

  const tryToReconnectMiddleware = useCallback(async () => {
    setRetryMiddlewareStatus(true);
    const res = await checkMiddleWareHealth();
    const timeoutId = setTimeout(() => {
      if (res) {
        setMiddlewareLiveStatus(String(res));
        setShowMiddlewarePopup(res);
        setRetryMiddlewareStatus(res);
      } else {
        setRetryMiddlewareStatus(false);
      }
    }, 20000);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const onUpdate = (registration) => {
      if (window.confirm("New version available! Would you like to update?")) {
        const worker = registration.waiting;
        if (worker) {
          worker.postMessage({ action: "skipWaiting" });
          const stateChangeHandler = (event) => {
            if (event.target.state === "activated") {
              window.location.reload(true);
            }
          };
          worker.addEventListener("statechange", stateChangeHandler);
          return () => worker.removeEventListener("statechange", stateChangeHandler);
        }
      }
    };

    const registration = serviceWorkerRegistration({ onUpdate });
    return () => {
      if (registration && registration.unregister) {
        registration.unregister();
      }
    };
  }, []);

  useEffect(() => {
    if (middlewareLiveStatus === null) {
      middlewareHealth();
    }

    middlewareIntervalRef.current = setInterval(() => {
      middlewareHealth();
    }, 60000);

    return () => {
      if (middlewareIntervalRef.current) {
        clearInterval(middlewareIntervalRef.current);
      }
    };
  }, [middlewareHealth, middlewareLiveStatus]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOnlinePopup(true);
      setIsTryingToConnect(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOnlinePopup(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (isOnline) {
      const timer = setTimeout(() => {
        setShowOnlinePopup(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  useEffect(() => {
    setShowStatusBar(showOnlinePopup || isTryingToConnect || middlewareLiveStatus === "false");
  }, [showOnlinePopup, isTryingToConnect, middlewareLiveStatus]);

  useEffect(() => {
    setDefaultLanguage(selectedLanguage);
  }, [selectedLanguage]);

  // Capture the beforeinstallprompt event for PWA installation
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const networkNotificationProps = useMemo(() => ({
    isOnline,
    isTryingToConnect,
    tryToReconnect,
    successText: onlineText[selectedLanguage],
    failureText: offlineText[selectedLanguage],
    tryingText: retryText[selectedLanguage],
    tryAgainText: tryAgainText[selectedLanguage]
  }), [isOnline, isTryingToConnect, tryToReconnect, selectedLanguage]);
  const middlewareNotificationProps = useMemo(() => ({
    isOnline: middlewareLiveStatus === "true",
    isTryingToConnect: retryMiddlewareStatus,
    tryToReconnect: tryToReconnectMiddleware,
    successText: liveText[selectedLanguage],
    failureText: showMiddlewarePopup === "modelDown" ? downText.modelDown[selectedLanguage] : 
                showMiddlewarePopup === "middlewareDown" ? downText.middlewareDown[selectedLanguage] : 
                downText.default[selectedLanguage],
    tryingText: retryLiveText[selectedLanguage],
    tryAgainText: tryAgainLiveText[selectedLanguage]
  }), [middlewareLiveStatus, retryMiddlewareStatus, tryToReconnectMiddleware, selectedLanguage, showMiddlewarePopup]);

  if (serverSchedullingFlag && isRestrictedTime()) {
    return <StaticHTML />;
  }

  return (
    <div translate="no" className={`h-screen-safe w-screen overflow-hidden ${isNeGDDeployment() ? 'iframe-theme' : 'pwa-theme'}`}>
      <Router>
        <NotificationManager />
        <Routes>
          <Route
            path="/chat"
            element={
              <ChatBody
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                textSize={textSize}
                decreaseTextSize={decreaseTextSize}
                increaseTextSize={increaseTextSize}
                audioRef={audioRef}
                stepIndex={stepIndex}
                setStepIndex={setStepIndex}
                startTour={startTour}
                setStartTour={setStartTour}
              />
            }
          />
          <Route
            path="/"
            element={
              <Splash
                setSelectedLanguage={setSelectedLanguage}
                setStepIndex={setStepIndex}
                setStartTour={setStartTour}
              />
            }
          />
          <Route
            path="/app-error"
            element={<AppError />}
          />
          <Route
            path="/lang"
            element={
              <IndicLang
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                audioRef={audioRef}
                setStepIndex={setStepIndex}
                setStartTour={setStartTour}
              />
            }
          />
          <Route
            path="/install"
            element={
              <PwaInstall 
                selectedLanguage={selectedLanguage}
                audioRef={audioRef}
              />
            }
          />
          <Route
            path="/login"
            element={
              <Introduction
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                audioRef={audioRef}
                setStepIndex={setStepIndex}
                setStartTour={setStartTour}
              />
              // <PhoneAuth />
            }
          />
          <Route
            path="/kb-selection"
            element={
              <KbSelection
                selectedLanguage={selectedLanguage}
                setStepIndex={setStepIndex}
                setStartTour={setStartTour}
              />
            }
          />
          <Route
            path="/register"
            element={
              <RegisterComponent
                selectedLanguage={selectedLanguage}
                audioRef={audioRef}
              />
            }
          />
          <Route
            path="/register-success"
            element={
              <RegisterSuccessComponent
                selectedLanguage={selectedLanguage}
              />
            }
          />
          <Route
            path="/guide"
            element={
              <Guide
                selectedLanguage={selectedLanguage}
                audioRef={audioRef}
                setStepIndex={setStepIndex}
                setStartTour={setStartTour}
              />
            }
          />
          <Route
            path="/consent"
            element={
              <Consent
                selectedLanguage={selectedLanguage}
                audioRef={audioRef}
                setStepIndex={setStepIndex}
                setStartTour={setStartTour}
              />
            }
          />
          <Route
            path="/language-check"
            element={
              <LanguageCheck
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
              />
            }
          />
          <Route
            path="/device_readiness/consent"
            element={
              <DeviceReadinessConsent
                selectedLanguage={selectedLanguage}
                audioRef={audioRef}
              />
            }
          />
          <Route
            path="/device_readiness/intro"
            element={
              <DeviceReadinessIntro
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                audioRef={audioRef}
              />
            }
          />
          <Route
            path="/device_readiness/form"
            element={
              <DeviceReadinessForm
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                audioRef={audioRef}
              />
            }
          />
          <Route
            path="/device_readiness/thanku"
            element={
              <DeviceReadinessEnd
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                audioRef={audioRef}
              />
            }
          />
          <Route
            path="/device_readiness/record"
            element={
              <DeviceReadinessRecord
                selectedLanguage={selectedLanguage}
              />
            }
          />
          <Route path="/admin/*" element={<AdminRoute />}>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<UserList />} />
            <Route path="users/new" element={<UserForm mode="create" />} />
            <Route path="users/:userId/edit" element={<UserForm mode="edit" />} />
            <Route path="import" element={<BulkImport />} />
            <Route path="notifications" element={<NotificationCampaign />} />
            {/* <Route path="audit-logs" element={<AuditLog />} /> */}
          </Route>
        </Routes>
      </Router>
      {(showOnlinePopup || isTryingToConnect) && (
        <NetworkNotification {...networkNotificationProps} />
      )}
      {showMiddlewarePopup !== null && (
        <NetworkNotification {...middlewareNotificationProps} />
      )}
      {SERVER_URL && /nonprod/i.test(SERVER_URL) && (
        <div className="non-prod-label">Non Prod</div>
      )}
    </div>
  );
}

export default App;
